import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { VaTextInput } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import environment from 'platform/utilities/environment';
import { updateZipCache, clearZipCache } from '../utils/zipCodeData';

export default function ZipCodeInput(props) {
  const { childrenProps, label, required, error } = props;
  // The zip whose validation request is currently out (or just resolved) —
  // lets a resolved fetch tell whether it's still about the zip that matters.
  const prevZipQuery = useRef(null);
  // The exact current field text, updated on every keystroke, so an async
  // fetch callback can read what the user has typed since it was fired.
  const currentZipFieldVal = useRef('');

  const handleInput = event => {
    const raw = event.target.value;
    // event.target.value is always a string, so '' is the only falsy case.
    const newVal = raw || undefined;
    currentZipFieldVal.current = raw;
    childrenProps.onChange(newVal);

    const zip = raw.slice(0, 5);

    if (!/^\d{5}$/.test(zip)) {
      prevZipQuery.current = null;
      clearZipCache();
      return;
    }

    if (zip === prevZipQuery.current) return;

    prevZipQuery.current = zip;
    clearZipCache();

    fetch(`${environment.API_URL}/validations/v0/zipcode/${zip}`, {
      credentials: 'include',
    })
      .then(r => {
        if (!r.ok) {
          throw new Error(`Zip code validation failed (${r.status})`);
        }
        return r.json();
      })
      .then(resp => {
        // Ignore stale responses; a newer zip has since been entered.
        if (prevZipQuery.current !== zip) return;

        const isValid = resp?.zip_is_valid;
        if (typeof isValid !== 'boolean') {
          throw new Error(
            'Zip code validation response missing zip_is_valid boolean',
          );
        }

        updateZipCache(zip, isValid);

        // Re-trigger validation without clobbering any newer user input.
        childrenProps.onChange(currentZipFieldVal.current || undefined);
      })
      .catch(err => {
        if (prevZipQuery.current !== zip) return;
        clearZipCache();
        window.DD_RUM?.addError(err, { source: 'zip_code_validation' });
      });
  };

  return (
    <VaTextInput
      id={childrenProps.idSchema.$id}
      name={childrenProps.idSchema.$id}
      label={label}
      required={required}
      error={error}
      value={childrenProps.formData ?? ''}
      maxlength={childrenProps.schema?.maxLength}
      onInput={handleInput}
      onBlur={() => childrenProps.onBlur(childrenProps.idSchema.$id)}
      uswds
    />
  );
}

ZipCodeInput.propTypes = {
  childrenProps: PropTypes.shape({
    formData: PropTypes.string,
    idSchema: PropTypes.shape({ $id: PropTypes.string.isRequired }).isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    schema: PropTypes.shape({ maxLength: PropTypes.number }),
  }).isRequired,
  error: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
};
